import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { X, MessageSquare } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore } from '@/store'
import { messages, threadMessages, users } from '@/mocks'
import { MessageItem } from '../app/message/MessageItem'

const Panel = styled(View, {
  width: 320,
  backgroundColor: '$sidebar',
  flexDirection: 'column',
  flexShrink: 0,
  borderLeftWidth: 1,
  borderLeftColor: '$borderDefault',
})

const PanelHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const HeaderTitle = styled(Text, {
  fontFamily: '$fontBody',
  fontWeight: '600',
  fontSize: 15,
  color: '$surfaceForeground',
})

const CloseButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const ThreadDivider = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: 16,
  paddingVertical: 8,
  gap: 8,
})

const DividerLine = styled(View, {
  flex: 1,
  height: 1,
  backgroundColor: '$borderDefault',
})

const DividerText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  color: '$color8',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
})

const NewThreadForm = styled(View, {
  padding: 16,
  gap: 12,
  flex: 1,
})

const CreateThreadButton = styled(View, {
  height: 36,
  paddingHorizontal: 16,
  borderRadius: '$borderRadiusMd',
  backgroundColor: '$accent',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  alignSelf: 'flex-start',
  hoverStyle: { opacity: 0.9 },
})

export function ThreadPanel() {
  const { activeThreadMessageId, activeChannelId, closeThread } = useAppStore()
  const theme = useTheme()
  const [threadTitle, setThreadTitle] = useState('')
  const [threadMessage, setThreadMessage] = useState('')

  if (!activeThreadMessageId || !activeChannelId) return null

  // Find the parent message
  const channelMessages = messages[activeChannelId] || []
  const parentMessage = channelMessages.find(m => m.id === activeThreadMessageId)
  if (!parentMessage) return null

  // Get thread replies
  const replies = threadMessages[activeThreadMessageId] || []
  const isNewThread = replies.length === 0

  return (
    <Panel>
      <PanelHeader>
        <HeaderTitle>{isNewThread ? 'Start a Thread' : 'Thread'}</HeaderTitle>
        <CloseButton onPress={closeThread}>
          <X size={16} color="$sidebarMuted" />
        </CloseButton>
      </PanelHeader>

      <ScrollView flex={1} showsVerticalScrollIndicator={false}>
        <View padding={8}>
          {/* Parent message */}
          <MessageItem
            message={parentMessage}
            user={users[parentMessage.authorId]}
            showAvatar
            showActions={false}
          />

          {/* Divider */}
          <ThreadDivider>
            <DividerLine />
            <DividerText>
              {isNewThread ? 'new thread' : `${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}
            </DividerText>
            <DividerLine />
          </ThreadDivider>

          {isNewThread ? (
            <NewThreadForm>
              <View alignItems="center" gap={8} paddingBottom={8}>
                <MessageSquare size={24} color="$color8" />
                <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted" textAlign="center">
                  Start a conversation about this message
                </Text>
              </View>

              <View gap={4}>
                <Text fontFamily="$fontBody" fontSize={12} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={0.5}>
                  Thread Title
                </Text>
                <input
                  value={threadTitle}
                  onChange={(e) => setThreadTitle(e.target.value)}
                  placeholder="Give this thread a title..."
                  style={{
                    background: 'transparent',
                    border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                    borderRadius: 6,
                    padding: '8px 12px',
                    color: theme.surfaceForeground?.val || '#E2E8F0',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              </View>

              <View gap={4}>
                <Text fontFamily="$fontBody" fontSize={12} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={0.5}>
                  Message
                </Text>
                <textarea
                  value={threadMessage}
                  onChange={(e) => setThreadMessage(e.target.value)}
                  placeholder="Write your first reply..."
                  rows={4}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                    borderRadius: 6,
                    padding: '8px 12px',
                    color: theme.surfaceForeground?.val || '#E2E8F0',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    outline: 'none',
                    resize: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              </View>

              <CreateThreadButton>
                <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Create Thread</Text>
              </CreateThreadButton>
            </NewThreadForm>
          ) : (
            /* Thread replies */
            replies.map((reply, index) => {
              const prevReply = index > 0 ? replies[index - 1] : null
              const showAvatar = !prevReply || prevReply.authorId !== reply.authorId
              return (
                <MessageItem
                  key={reply.id}
                  message={reply}
                  user={users[reply.authorId]}
                  showAvatar={showAvatar}
                  showActions={false}
                />
              )
            })
          )}
        </View>
      </ScrollView>
    </Panel>
  )
}
